import { createContext, useContext, useState, useMemo } from "react";
import type { ReactNode } from "react";
import type { RenderContentFn, TemplateAction, TemplateNodeConfig, LayoutConfig } from "./types";

export interface NullContentContextValue {
  addNullContent: (contentId: number) => void;
  removeNullContent: (contentId: number) => void;
}

export interface TemplateContextValue {
  renderContent: RenderContentFn;
  renderTemplate: (config: TemplateNodeConfig, layout?: LayoutConfig, contentId?: number) => ReactNode;
  onAction: (action: TemplateAction) => void;
  loading?: boolean;
  localContext?: unknown;
}

export const NullContentContext =
  createContext<NullContentContextValue | null>(null);

export const TemplateContext =
  createContext<TemplateContextValue | null>(null);

export function useTemplate() {
  const ctx = useContext(TemplateContext);
  if (!ctx) throw new Error("useTemplate must be used within Template");
  return ctx;
}

export function useNullContent() {
  const [nullContent, setNullContent] = useState<number[]>([]);

  const context = useMemo<NullContentContextValue>(
    () => ({
      addNullContent: (contentId) =>
        setNullContent((c) => (c.includes(contentId) ? c : [...c, contentId])),
      removeNullContent: (contentId) =>
        setNullContent((c) => c.filter((id) => id !== contentId)),
    }),
    [],
  );

  return { nullContent, context };
}
